import { z } from 'zod';
import {
  LLMMessageRoleSchema,
  type LLMMessageRole,
  type WorldBookGlobalSettings,
  WorldBookGlobalSettingsSchema,
  WorldBookInjectionModeSchema,
  type WorldBookInjectionMode,
  WorldBookMatchLogicSchema,
} from './base';

export const WorldBookEntrySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).default('新世界书条目'),
  enabled: z.boolean().default(true),
  constant: z.boolean().default(false),
  content: z.string().default(''),
  keys: z.array(z.string().min(1)).default([]),
  secondary_keys: z.array(z.string().min(1)).default([]),
  match_logic: WorldBookMatchLogicSchema.default('any'),
  case_sensitive: z.boolean().default(false),
  whole_word: z.boolean().default(false),
  role: LLMMessageRoleSchema.default('system'),
  injection_mode: WorldBookInjectionModeSchema.default('relative_before'),
  injection_depth: z.number().int().min(0).default(2),
  injection_order: z.number().int().default(100),
  outlet_name: z.string().default(''),
  generation_trigger: z.array(z.string().min(1)).default([]),
  scan_depth: z.number().int().min(0).nullable().default(2),
  sticky: z.number().int().min(0).default(0),
  cooldown: z.number().int().min(0).default(0),
  delay: z.number().int().min(0).default(0),
  exclude_recursion: z.boolean().default(false),
  prevent_recursion: z.boolean().default(false),
  delay_until_recursion: z.boolean().default(false),
  use_probability: z.boolean().default(false),
  probability: z.number().int().min(0).max(100).default(100),
  character_filter: z
    .object({
      isExclude: z.boolean().default(false),
      names: z.array(z.string().min(1)).default([]),
      tags: z.array(z.string().min(1)).default([]),
    })
    .default({
      isExclude: false,
      names: [],
      tags: [],
    }),
});
export type WorldBookEntry = z.infer<typeof WorldBookEntrySchema>;

export const WorldBookSchema = z.object({
  enabled: z.boolean().default(false),
  global_settings: WorldBookGlobalSettingsSchema.default({
    scan_depth: 2,
    recursive_scan: true,
    max_recursion_rounds: null,
  }),
  entries: z.array(WorldBookEntrySchema).default([]),
});
export type WorldBook = z.infer<typeof WorldBookSchema>;

export const WorldBookConfigSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).default('默认世界书'),
  worldBook: WorldBookSchema.default({
    enabled: false,
    global_settings: {
      scan_depth: 2,
      recursive_scan: true,
      max_recursion_rounds: null,
    },
    entries: [],
  }),
});
export type WorldBookConfig = z.infer<typeof WorldBookConfigSchema>;

export function importSTWorldBook(raw: unknown): WorldBook {
  const source = raw as Record<string, unknown>;
  const worldInfo = Array.isArray(source.world_info) ? source.world_info : [];
  const entries = worldInfo.map((entry, index) => mapWorldBookEntry(entry as Record<string, unknown>, index));

  return WorldBookSchema.parse({
    enabled: entries.length > 0,
    global_settings: mapWorldBookGlobalSettings(source),
    entries,
  });
}

export function importWorldBook(raw: unknown): WorldBook {
  const source = raw as Record<string, unknown>;
  const topEntries = source?.entries;
  const dataEntries = (source?.data as Record<string, unknown> | undefined)?.entries;
  const fallbackEntries = (Array.isArray(topEntries) || isObjectMap(topEntries) ? topEntries : dataEntries) as
    | unknown
    | undefined;

  const entryList = normalizeEntryList(fallbackEntries);
  const entries = entryList.map((entry, index) => mapWorldBookEntry(entry, index));

  return WorldBookSchema.parse({
    enabled: entries.length > 0,
    global_settings: mapWorldBookGlobalSettings(source),
    entries,
  });
}

function normalizeEntryList(rawEntries: unknown): Record<string, unknown>[] {
  if (Array.isArray(rawEntries)) {
    return rawEntries.filter(item => item && typeof item === 'object') as Record<string, unknown>[];
  }

  if (isObjectMap(rawEntries)) {
    return Object.values(rawEntries).filter(item => item && typeof item === 'object') as Record<string, unknown>[];
  }

  return [];
}

function mapWorldBookEntry(entry: Record<string, unknown>, index: number): WorldBookEntry {
  const extensions = (entry.extensions ?? {}) as Record<string, unknown>;
  const rawPosition = entry.position ?? extensions.position;
  const rawRole = entry.role ?? extensions.role;
  const rawDepth = entry.depth ?? extensions.depth;
  const rawOrder = entry.order ?? entry.insertion_order ?? extensions.insertion_order ?? 100;
  const rawTriggers = entry.triggers ?? extensions.triggers;
  const rawConstant = entry.constant ?? extensions.constant;
  const rawOutlet = entry.outletName ?? entry.outlet_name ?? extensions.outlet_name ?? extensions.outletName;
  const rawExcludeRecursion = entry.excludeRecursion ?? entry.exclude_recursion ?? extensions.exclude_recursion;
  const rawPreventRecursion = entry.preventRecursion ?? entry.prevent_recursion ?? extensions.prevent_recursion;
  const rawDelayUntilRecursion =
    entry.delayUntilRecursion ?? entry.delay_until_recursion ?? extensions.delay_until_recursion;
  const rawUseProbability =
    entry.useProbability ?? entry.use_probability ?? extensions.useProbability ?? extensions.use_probability;
  const rawProbability = entry.probability ?? extensions.probability;
  const rawScanDepth = entry.scanDepth ?? entry.scan_depth ?? extensions.scan_depth;
  const rawSticky = entry.sticky ?? extensions.sticky;
  const rawCooldown = entry.cooldown ?? extensions.cooldown;
  const rawDelay = entry.delay ?? extensions.delay;
  const rawCharacterFilter = entry.characterFilter ?? entry.character_filter;
  const caseSensitive = entry.caseSensitive ?? extensions.case_sensitive ?? false;
  const wholeWords = entry.matchWholeWords ?? extensions.match_whole_words ?? false;
  const normalizedProbability = normalizeProbability(rawProbability, 100);

  return WorldBookEntrySchema.parse({
    id: String(entry.id ?? entry.uid ?? `wi_${index + 1}`),
    name: String(entry.comment ?? entry.name ?? `世界书 ${index + 1}`),
    enabled: resolveEntryEnabled(entry),
    constant: normalizeBoolean(rawConstant, false),
    content: String(entry.content ?? ''),
    keys: normalizeStringArray(entry.key ?? entry.keys),
    secondary_keys: normalizeStringArray(entry.keysecondary ?? entry.secondary_keys),
    match_logic: Number(entry.selectiveLogic ?? extensions.selectiveLogic ?? 0) === 3 ? 'all' : 'any',
    case_sensitive: Boolean(caseSensitive),
    whole_word: Boolean(wholeWords),
    role: normalizeRole(rawRole),
    injection_mode: normalizeInjectionMode(rawPosition),
    injection_depth: normalizeInt(rawDepth, 2),
    injection_order: normalizeInt(rawOrder, 100),
    outlet_name: typeof rawOutlet === 'string' ? rawOutlet : '',
    generation_trigger: normalizeStringArray(rawTriggers),
    scan_depth: rawScanDepth === undefined ? undefined : normalizeNullableInt(rawScanDepth),
    sticky: normalizeInt(rawSticky, 0),
    cooldown: normalizeInt(rawCooldown, 0),
    delay: normalizeInt(rawDelay, 0),
    exclude_recursion: normalizeBoolean(rawExcludeRecursion, false),
    prevent_recursion: normalizeBoolean(rawPreventRecursion, false),
    delay_until_recursion: normalizeBoolean(rawDelayUntilRecursion, false),
    use_probability: normalizeImportedUseProbability(rawUseProbability, normalizedProbability),
    probability: normalizedProbability,
    character_filter: normalizeCharacterFilter(rawCharacterFilter),
  });
}

function mapWorldBookGlobalSettings(source: Record<string, unknown>): WorldBookGlobalSettings {
  const extensions = (source.extensions ?? {}) as Record<string, unknown>;
  const rawScanDepth =
    source.world_info_depth ?? source.scan_depth ?? extensions.world_info_depth ?? extensions.scan_depth;
  const rawRecursive =
    source.world_info_recursive ??
    source.recursive_scan ??
    extensions.world_info_recursive ??
    extensions.recursive_scan;
  const rawMaxRounds =
    source.world_info_max_recursion_rounds ??
    source.max_recursion_rounds ??
    extensions.world_info_max_recursion_rounds ??
    extensions.max_recursion_rounds;

  return WorldBookGlobalSettingsSchema.parse({
    scan_depth: rawScanDepth === undefined ? undefined : normalizeNullableInt(rawScanDepth),
    recursive_scan: rawRecursive === undefined ? undefined : normalizeBoolean(rawRecursive, true),
    max_recursion_rounds: rawMaxRounds === undefined ? undefined : normalizeNullableInt(rawMaxRounds),
  });
}

function resolveEntryEnabled(entry: Record<string, unknown>): boolean {
  if (typeof entry.enabled === 'boolean') return entry.enabled;
  if (typeof entry.disable === 'boolean') return !entry.disable;
  return true;
}

function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map(item => String(item).trim()).filter(Boolean);
}

function normalizeInt(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
}

function normalizeBoolean(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') return value;
  if (value === 1 || value === '1') return true;
  if (value === 0 || value === '0') return false;
  return fallback;
}

function normalizeNullableInt(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.trunc(n));
}

function normalizeProbability(value: unknown, fallback: number): number {
  const n = normalizeInt(value, fallback);
  return Math.max(0, Math.min(100, n));
}

function normalizeImportedUseProbability(value: unknown, probability: number): boolean {
  if (value === undefined || value === null || value === '') {
    return probability < 100;
  }

  return normalizeBoolean(value, false) && probability < 100;
}

function normalizeRole(value: unknown): LLMMessageRole {
  if (value === 1 || value === '1' || value === 'user') return 'user';
  if (value === 2 || value === '2' || value === 'assistant') return 'assistant';
  return 'system';
}

function normalizeInjectionMode(value: unknown): WorldBookInjectionMode {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase();

  if (normalized === 'before_char' || normalized === 'relative_before' || normalized === '0') {
    return 'relative_before';
  }

  if (normalized === 'after_char' || normalized === 'relative_after' || normalized === '1') {
    return 'relative_after';
  }

  if (normalized === 'before_example' || normalized === '2') {
    return 'before_example';
  }

  if (normalized === 'after_example' || normalized === '3') {
    return 'after_example';
  }

  if (normalized === 'in_chat' || normalized === 'depth' || normalized === '@d' || normalized === '4') {
    return 'depth';
  }

  if (normalized === 'before_an' || normalized === 'before_author_note' || normalized === '5') {
    return 'before_author_note';
  }

  if (normalized === 'after_an' || normalized === 'after_author_note' || normalized === '6') {
    return 'after_author_note';
  }

  if (normalized === 'outlet' || normalized === '7') {
    return 'outlet';
  }

  return 'relative_before';
}

function normalizeCharacterFilter(value: unknown): {
  isExclude: boolean;
  names: string[];
  tags: string[];
} {
  const source = (value ?? {}) as Record<string, unknown>;
  return {
    isExclude: normalizeBoolean(source.isExclude, false),
    names: normalizeStringArray(source.names),
    tags: normalizeStringArray(source.tags),
  };
}

function isObjectMap(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
